<div class="my-project-content">
  <!-- Shared Create Notebook fields (tab + modal) -->
  <ng-template #notebookCreateWorkspace let-fieldPrefix="fieldPrefix">
  <!-- ══ Horizontal stepper ══════════════════════════════════════════════ -->
  <div class="nb-hstepper">

    <!-- Navigation track (matches Add Dataset stepper) -->
    <div class="nb-hstepper__nav" role="tablist" aria-label="Create notebook steps">
      <div
        *ngFor="let step of notebookCreateSteps; let i = index"
        class="nb-hstepper__step"
        role="tab"
        [attr.tabindex]="isNotebookStepTrackClickable(i) ? 0 : -1"
        [class.nb-hstepper__step--active]="isNotebookStepTrackActive(i)"
        [class.nb-hstepper__step--completed]="isNotebookStepTrackCompleted(i)"
        [class.nb-hstepper__step--clickable]="isNotebookStepTrackClickable(i)"
        [attr.aria-selected]="isNotebookStepTrackActive(i)"
        (click)="goToStep(i + 1)"
      >
        <div class="nb-hstepper__step-circle">
          <svg
            *ngIf="isNotebookStepTrackCompleted(i)"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M20 6L9 17L4 12"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span *ngIf="!isNotebookStepTrackCompleted(i)">{{ i + 1 }}</span>
        </div>
        <span class="nb-hstepper__step-label">{{ step.label }}</span>
        <div
          *ngIf="i < notebookCreateSteps.length - 1"
          class="nb-hstepper__step-connector"
          [class.nb-hstepper__step-connector--completed]="isNotebookStepTrackConnectorCompleted(i)"
        ></div>
      </div>
    </div><!-- /nb-hstepper__nav -->

    <!-- Content panel -->
    <div class="nb-hstepper__body">
      <!-- Step 1: Instance Type -->
      <div class="nb-hstepper__panel" *ngIf="currentStep === 1">
        <div class="nb-hstepper__panel-heading">
          <h2 class="nb-hstepper__panel-title">Choose instance type</h2>
          <p class="nb-hstepper__panel-desc">Select the compute hardware for your notebook session.</p>
        </div>
        <div class="instance-type-options">
          <div *ngFor="let option of instanceTypeOptions" class="instance-type-option-wrapper">
            <div class="instance-type-option"
                 [class.selected]="notebookForm.get('instanceType')?.value === option.value"
                 [class.disabled]="option.value === 'GPU' && isGpuDisabled()"
                 (click)="selectNotebookInstanceKind(option.value)">

              <!-- Checkmark badge -->
              <span class="instance-type-option__check"
                    *ngIf="notebookForm.get('instanceType')?.value === option.value"
                    aria-hidden="true">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none"><path d="M5 12l5 5L20 7" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </span>

              <!-- Icon + label row -->
              <div class="instance-type-option__top">
                <div class="instance-type-option__icon" [ngSwitch]="option.value">
                  <svg *ngSwitchCase="'CPU'" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" stroke-width="1.8"/>
                    <rect x="9" y="9" width="6" height="6" rx="0.8" fill="currentColor"/>
                    <path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                  </svg>
                  <svg *ngSwitchCase="'GPU'" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect x="2" y="7" width="18" height="10" rx="2" stroke="currentColor" stroke-width="1.8"/>
                    <path d="M20 10h2M20 14h2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                    <rect x="5" y="10" width="3" height="4" rx="0.6" fill="currentColor"/>
                    <rect x="10" y="10" width="3" height="4" rx="0.6" fill="currentColor"/>
                    <path d="M7 17v2M13 17v2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                  </svg>
                  <svg *ngSwitchCase="'LOCAL'" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect x="3" y="5" width="18" height="12" rx="2" stroke="currentColor" stroke-width="1.8"/>
                    <path d="M8 20h8M12 17v3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                    <circle cx="12" cy="11" r="2.5" stroke="currentColor" stroke-width="1.6"/>
                  </svg>
                  <svg *ngSwitchDefault viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8"/>
                  </svg>
                </div>
                <div class="instance-type-option__body">
                  <div class="instance-type-label">{{ option.label }}</div>
                  <div class="instance-type-sublabel">
                    <ng-container *ngIf="option.value === 'CPU'">Standard compute for most workloads</ng-container>
                    <ng-container *ngIf="option.value === 'GPU'">Accelerated compute for ML &amp; training</ng-container>
                    <ng-container *ngIf="option.value === 'LOCAL'">{{ getLocalBrowserInstanceSublabel() }}</ng-container>
                    <ng-container *ngIf="option.value !== 'CPU' && option.value !== 'GPU' && option.value !== 'LOCAL'">High-performance compute instance</ng-container>
                  </div>
                </div>
              </div>

              <!-- Divider -->
              <div class="instance-type-option__divider"></div>

              <!-- Tag row -->
              <div class="instance-type-option__tags">
                <ng-container [ngSwitch]="option.value">
                  <ng-container *ngSwitchCase="'CPU'">
                    <span class="instance-type-tag instance-type-tag--green">General Purpose</span>
                    <span class="instance-type-tag instance-type-tag--neutral">Shared</span>
                  </ng-container>
                  <ng-container *ngSwitchCase="'GPU'">
                    <span class="instance-type-tag instance-type-tag--amber">ML / Training</span>
                    <span class="instance-type-tag instance-type-tag--neutral">Dedicated</span>
                  </ng-container>
                  <ng-container *ngSwitchCase="'LOCAL'">
                    <span class="instance-type-tag instance-type-tag--green">Free</span>
                    <span class="instance-type-tag instance-type-tag--neutral">Browser-only</span>
                  </ng-container>
                  <ng-container *ngSwitchDefault>
                    <span class="instance-type-tag instance-type-tag--neutral">Compute</span>
                  </ng-container>
                </ng-container>
              </div>

              <div class="instance-type-radio" aria-hidden="true">
                <div class="radio-circle" [class.checked]="notebookForm.get('instanceType')?.value === option.value">
                  <svg *ngIf="notebookForm.get('instanceType')?.value === option.value" width="10" height="10" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="5" fill="currentColor"/></svg>
                </div>
              </div>
            </div>
            <div *ngIf="option.value === 'GPU' && !hasActiveComputeRole" class="gpu-warning-message">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              <span *ngIf="hasPendingComputeRole()">Your Compute Role upgrade request is currently under review. Please wait for <span class="approval-highlight">Approval</span>.</span>
              <span *ngIf="!hasPendingComputeRole()">You must have Compute Access in order to make GPU Request.</span>
            </div>
          </div>
        </div>
        <div *ngIf="notebookForm.get('instanceType')?.invalid && notebookForm.get('instanceType')?.touched" class="error-message">Instance type is required</div>
        <div class="nb-step-actions nb-step-actions--first">
          <button
            type="button"
            class="nb-name-ready__btn"
            [disabled]="!isStepCompleted(1)"
            (click)="onNotebookInstanceTypeCommit()"
          >
            Continue
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        </div>
      </div>

      <!-- Step 2: Notebook Name -->
      <div class="nb-hstepper__panel" *ngIf="currentStep === 2">
        <div class="nb-hstepper__panel-heading">
          <h2 class="nb-hstepper__panel-title">Give your notebook a name</h2>
          <p class="nb-hstepper__panel-desc">Choose a unique, lowercase identifier for your session.</p>
        </div>
        <div class="form-field nb-hstepper__name-field">
          <label class="form-label" [attr.for]="fieldPrefix + '-nb-name'">
            Name <span class="required">*</span>
          </label>
          <input
            type="text"
            [id]="fieldPrefix + '-nb-name'"
            [formControl]="$any(notebookForm.get('name'))"
            class="form-input"
            [class.input-error]="shouldShowNotebookNameErrors()"
            [class.input-valid]="notebookForm.get('name')?.valid && notebookForm.get('name')?.value"
            placeholder="e.g. my-cv-notebook"
            [attr.maxlength]="MAX_NOTEBOOK_NAME_LENGTH"
            autocomplete="off"
            (blur)="onNotebookNameBlur()"
            (keydown.enter)="$event.preventDefault()"
          />
          <div *ngIf="shouldShowNotebookNameErrors()" class="error-message">
            {{ getNotebookNameErrors() }}
          </div>
          <div class="validation-rules" *ngIf="notebookForm.get('name')?.value || notebookForm.get('name')?.touched">
            <div class="validation-rules-title">Validation Rules:</div>
            <ul class="validation-rules-list">
              <li [class.rule-valid]="isNotebookNameLengthValid()" [class.rule-invalid]="notebookForm.get('name')?.value && !isNotebookNameLengthValid()">
                <svg class="rule-icon rule-icon--pass" *ngIf="isNotebookNameLengthValid()" width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.15"/><path d="M8 12l3 3 5-6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                <svg class="rule-icon rule-icon--fail" *ngIf="notebookForm.get('name')?.value && !isNotebookNameLengthValid()" width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.12"/><path d="M15 9l-6 6M9 9l6 6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>
                4–{{ MAX_NOTEBOOK_NAME_LENGTH }} characters
              </li>
              <li [class.rule-valid]="isLowercaseOnlyValid()" [class.rule-invalid]="notebookForm.get('name')?.value && !isLowercaseOnlyValid()">
                <svg class="rule-icon rule-icon--pass" *ngIf="isLowercaseOnlyValid()" width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.15"/><path d="M8 12l3 3 5-6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                <svg class="rule-icon rule-icon--fail" *ngIf="notebookForm.get('name')?.value && !isLowercaseOnlyValid()" width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.12"/><path d="M15 9l-6 6M9 9l6 6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>
                Only lowercase letters, numbers, hyphens
              </li>
              <li [class.rule-valid]="isStartsWithLetterOrNumberValid()" [class.rule-invalid]="notebookForm.get('name')?.value && !isStartsWithLetterOrNumberValid()">
                <svg class="rule-icon rule-icon--pass" *ngIf="isStartsWithLetterOrNumberValid()" width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.15"/><path d="M8 12l3 3 5-6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                <svg class="rule-icon rule-icon--fail" *ngIf="notebookForm.get('name')?.value && !isStartsWithLetterOrNumberValid()" width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.12"/><path d="M15 9l-6 6M9 9l6 6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>
                Must start with a letter or number
              </li>
              <li [class.rule-valid]="isHyphenValid()" [class.rule-invalid]="notebookForm.get('name')?.value && !isHyphenValid()">
                <svg class="rule-icon rule-icon--pass" *ngIf="isHyphenValid()" width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.15"/><path d="M8 12l3 3 5-6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                <svg class="rule-icon rule-icon--fail" *ngIf="notebookForm.get('name')?.value && !isHyphenValid()" width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.12"/><path d="M15 9l-6 6M9 9l6 6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>
                Hyphens only between characters (not at start/end; no consecutive hyphens)
              </li>
            </ul>
          </div>
        </div>
        <div class="nb-step-actions">
          <button type="button" class="nb-hstepper__back-btn" (click)="previousStep()">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M14.5 17.5L9.5 12.5L14.5 7.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Back
          </button>
          <button
            type="button"
            class="nb-name-ready__btn"
            [disabled]="!isStepCompleted(2)"
            (click)="onNotebookNameCommit()"
          >
            Continue
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        </div>
      </div>

      <!-- Step 3: Categories -->
      <div class="nb-hstepper__panel" *ngIf="currentStep === 3">
        <div class="nb-hstepper__panel-heading">
          <h2 class="nb-hstepper__panel-title">Select a Category</h2>
          <p class="nb-hstepper__panel-desc">Pick the resource plan that best fits your workload.</p>
        </div>
        <div class="nb-local-skip-panel shell-surface" *ngIf="isLocalBrowserInstanceType()">
          <p class="nb-local-skip-panel__text">
            Browser notebooks use your computer&apos;s CPU and launch in a new tab.
            No category or session booking is required, and they will not appear in the Notebook list.
          </p>
          <button type="button" class="nb-name-ready__btn" (click)="currentStep = 4">
            Continue to agreement
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        </div>
        <div class="compute-plan-list" *ngIf="!isLocalBrowserInstanceType()">
          <button type="button" class="compute-plan-row"
                  *ngFor="let plan of getComputePlansForCurrentInstanceType()"
                  [class.compute-plan-row--selected]="notebookForm.get('computePlanId')?.value === plan.id"
                  [class.compute-plan-row--disabled]="plan.apiType === 'GPU' && isGpuDisabled()"
                  [disabled]="plan.apiType === 'GPU' && isGpuDisabled()"
                  (click)="selectComputePlan(plan.id)"
                  [attr.aria-pressed]="notebookForm.get('computePlanId')?.value === plan.id">

            <!-- Radio checkbox -->
            <div class="compute-plan-row__radio" aria-hidden="true">
              <svg *ngIf="notebookForm.get('computePlanId')?.value === plan.id" width="10" height="10" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="6" fill="currentColor"/>
              </svg>
            </div>

            <!-- Icon -->
            <div class="compute-plan-row__icon" [ngSwitch]="plan.icon">
              <svg *ngSwitchCase="'chip'" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" stroke-width="1.8"/><rect x="9" y="9" width="6" height="6" rx="0.8" fill="currentColor"/></svg>
              <svg *ngSwitchCase="'layers'" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 3L3 7l9 4 9-4-9-4z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M3 12l9 4 9-4" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M3 17l9 4 9-4" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>
              <svg *ngSwitchCase="'display'" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="3" y="4" width="18" height="12" rx="2" stroke="currentColor" stroke-width="1.6"/><path d="M8 20h8M12 16v4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
              <svg *ngSwitchCase="'rocket'" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M14 3l3 3-4 10-3-3L14 3z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M6 15l-2 5 5-2" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M10 9l1 1" stroke="currentColor" stroke-width="1.6"/></svg>
              <svg *ngSwitchCase="'memory'" viewBox="0 0 24 24" fill="none" aria-hidden="true"><ellipse cx="12" cy="6" rx="6" ry="2.5" stroke="currentColor" stroke-width="1.6"/><path d="M6 6v10c0 1.4 2.7 2.5 6 2.5s6-1.1 6-2.5V6" stroke="currentColor" stroke-width="1.6"/><path d="M6 11c0 1.4 2.7 2.5 6 2.5s6-1.1 6-2.5" stroke="currentColor" stroke-width="1.6"/></svg>
              <svg *ngSwitchDefault viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8"/></svg>
            </div>

            <!-- Title + desc -->
            <div class="compute-plan-row__info">
              <span class="compute-plan-row__title">{{ plan.title }}</span>
              <span class="compute-plan-row__desc">{{ plan.description }}</span>
            </div>

            <!-- Limit pills -->
            <div class="compute-plan-row__limits" *ngIf="plan.bookingLimits?.length">
              <span class="compute-plan-row__limit-pill" *ngFor="let lim of plan.bookingLimits">
                <span class="compute-plan-row__limit-value">{{ lim.value }}</span>
                <span class="compute-plan-row__limit-label">{{ lim.label }}</span>
              </span>
            </div>
          </button>
        </div>
        <div *ngIf="!isLocalBrowserInstanceType() && notebookForm.get('computePlanId')?.invalid && notebookForm.get('computePlanId')?.touched" class="error-message">Please choose a category</div>
        <div class="nb-step-actions" *ngIf="!isLocalBrowserInstanceType()">
          <button type="button" class="nb-hstepper__back-btn" (click)="previousStep()">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M14.5 17.5L9.5 12.5L14.5 7.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Back
          </button>
          <button
            type="button"
            class="nb-name-ready__btn"
            [disabled]="!isStepCompleted(3)"
            (click)="onNotebookComputePlanCommit()"
          >
            Continue
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        </div>
      </div>

      <!-- Step 4: Date & Session (or policy for local browser) -->
      <div class="nb-hstepper__panel" *ngIf="currentStep === 4">
        <div class="nb-hstepper__panel-heading">
          <h2 class="nb-hstepper__panel-title">
            {{ isLocalBrowserInstanceType() ? 'Review &amp; agree' : 'Pick your date &amp; session' }}
          </h2>
          <p class="nb-hstepper__panel-desc">
            <ng-container *ngIf="isLocalBrowserInstanceType()">Review the details below, agree to the policy, then launch in your browser.</ng-container>
            <ng-container *ngIf="!isLocalBrowserInstanceType()">Select a date and sessions</ng-container>
          </p>
        </div>
        <div class="nb-local-browser-note shell-surface" *ngIf="isLocalBrowserInstanceType()">
          <ng-container *ngIf="isBrowserLaunchAvailable()">
            <p class="nb-local-browser-note__lead">
              This notebook runs on <strong>your device&apos;s CPU</strong> and opens in a new browser tab.
              No sandbox slot or session booking is required.
              It will <strong>not appear in the Notebook list</strong>, because the session runs locally on your device—not on the sandbox.
            </p>

            <section class="nb-local-browser-note__section" aria-labelledby="nb-local-browser-capabilities">
              <h3 id="nb-local-browser-capabilities" class="nb-local-browser-note__heading">What You Can Do</h3>
              <p>
                {{ browserCategory?.displayName || 'Jupyter Lite' }} opens a browser-based notebook environment. You can:
              </p>
              <ul class="nb-local-browser-note__list">
                <li>Create, open, edit, rename, and delete notebooks from the file browser</li>
                <li>Run Python code using the Pyodide kernel</li>
                <li>Use code cells, Markdown cells, outputs, and standard notebook editing controls</li>
                <li>Save notebooks and settings in the browser&apos;s local storage</li>
                <li>Upload files such as <code>.ipynb</code>, <code>.csv</code>, and <code>.txt</code></li>
                <li>Download notebooks and other files to your machine</li>
                <li>Read uploaded files in Python (for example, <code>pandas.read_csv("file.csv")</code>)</li>
                <li>Create basic plots with browser-compatible libraries such as <code>matplotlib</code></li>
                <li>Use common Python workflows supported by the browser runtime</li>
              </ul>
            </section>

            <section class="nb-local-browser-note__section" aria-labelledby="nb-local-browser-persistence">
              <h3 id="nb-local-browser-persistence" class="nb-local-browser-note__heading">Persistence</h3>
              <p>
                {{ browserCategory?.displayName || 'Jupyter Lite' }} files are stored by the browser.
                They are not synced to Sandbox Connect storage, Kubernetes PVCs, or user profiles.
              </p>
              <p>Because storage is browser-local:</p>
              <ul class="nb-local-browser-note__list">
                <li>Files usually stay available in the same browser profile on the same device</li>
                <li>Files may not appear in a different browser, device, private/incognito session, or domain</li>
                <li>Clearing site data, browser storage, or cache can remove notebooks and files</li>
                <li>Download important notebooks when you need a copy outside that browser profile</li>
              </ul>
            </section>
          </ng-container>
          <p *ngIf="!isBrowserLaunchAvailable()" class="error-message">
            Browser notebook is not available right now. Please refresh or try again later.
          </p>
        </div>
        <div class="notebook-scheduler" *ngIf="!isLocalBrowserInstanceType()">
          <div class="notebook-scheduler__calendar shell-surface">
            <div class="notebook-cal-loading-bar" *ngIf="calendarLoading" aria-hidden="true"></div>
            <div class="notebook-cal-header">
              <button type="button" class="notebook-cal-nav" (click)="advanceScheduleCalendarMonth(-1)" aria-label="Previous month"
                      [disabled]="isCalendarBackDisabled">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M14.5 17.5L9.5 12.5L14.5 7.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              <span class="notebook-cal-month">{{ scheduleCalendarTitle }}</span>
              <button type="button" class="notebook-cal-nav" (click)="advanceScheduleCalendarMonth(1)" aria-label="Next month"
                      [disabled]="isCalendarForwardDisabled">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M9.5 7.5l5 5-5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
            </div>
            <div class="notebook-cal-weekdays"><span *ngFor="let wd of weekDayLabelsShort">{{ wd }}</span></div>
            <div class="notebook-cal-grid">
              <ng-container *ngFor="let week of getScheduleCalendarWeeks()">
                <button *ngFor="let cell of week" type="button" class="notebook-cal-day"
                        [class.notebook-cal-day--muted]="!cell.inMonth"
                        [class.notebook-cal-day--today]="cell.key && isNotebookScheduleTodayKey(cell.key)"
                        [class.notebook-cal-day--selected]="isNotebookScheduleCalendarCellSelected(cell)"
                        [class.notebook-cal-day--blocked]="cell.inMonth && cell.date && (isNotebookSchedulePastDate(cell.date) || isNotebookScheduleBeyondWindow(cell.date) || (cell.key !== null && isNotebookScheduleDayFullyBooked(cell.key)))"
                        [disabled]="!cell.inMonth || !cell.date || isNotebookSchedulePastDate(cell.date!) || isNotebookScheduleBeyondWindow(cell.date!) || (cell.key !== null && isNotebookScheduleDayFullyBooked(cell.key!))"
                        (click)="pickNotebookScheduleCalendarCell(cell)">
                  <ng-container *ngIf="cell.date">
                    <span class="notebook-cal-day__num">{{ cell.date.getDate() }}</span>
                    <span *ngIf="cell.key && notebookScheduleAvailabilityDotClass(cell.key, cell.inMonth) === 'open'" class="notebook-cal-dot notebook-cal-dot--open"></span>
                    <span *ngIf="cell.key && notebookScheduleAvailabilityDotClass(cell.key, cell.inMonth) === 'booked'" class="notebook-cal-dot notebook-cal-dot--booked"></span>
                  </ng-container>
                </button>
              </ng-container>
            </div>
            <ul class="notebook-cal-legend">
              <li><span class="notebook-cal-dot notebook-cal-dot--open"></span> Slots open</li>
              <li><span class="notebook-cal-dot notebook-cal-dot--booked"></span> Fully booked day</li>
            </ul>
          </div>

          <div class="notebook-scheduler__slots shell-surface">
            <h3 class="notebook-slot-panel__title">
              <ng-container *ngIf="getScheduleSelectedDateForPipe() as sd">Available sessions for {{ sd | date:'EEE, MMM d' }}</ng-container>
              <ng-container *ngIf="!notebookForm.get('scheduleDate')?.value">Pick a date to view sessions</ng-container>
            </h3>
            <div class="notebook-slot-loading" *ngIf="slotsLoading" aria-busy="true">
              <span class="notebook-slot-loading__bar"></span>
              <span class="notebook-slot-loading__bar"></span>
              <span class="notebook-slot-loading__bar"></span>
            </div>

            <!-- Grouped layout: Passed chips + Available cards -->
            <ng-container *ngIf="notebookForm.get('scheduleDate')?.value && !slotsLoading && availableSlots.length > 0">

              <!-- Passed slots — collapsed chips -->
              <div class="notebook-slot-group" *ngIf="pastSlots.length > 0">
                <div class="notebook-slot-group__header">
                  <span class="notebook-slot-group__label">Passed</span>
                  <div class="notebook-slot-group__line"></div>
                </div>
                <div class="notebook-slot-passed-row">
                  <span *ngFor="let slot of pastSlots" class="notebook-slot-chip">
                    {{ slot.startTime }}–{{ slot.endTime }}
                  </span>
                </div>
              </div>

              <!-- Available slots — prominent cards -->
              <div class="notebook-slot-group" *ngIf="upcomingSlots.length > 0">
                <div class="notebook-slot-group__header">
                  <span class="notebook-slot-group__label">Available</span>
                  <div class="notebook-slot-group__line"></div>
                </div>
                <div class="notebook-slot-grid">
                  <div *ngFor="let slot of upcomingSlots"
                       class="notebook-slot-tile-host"
                       [class.notebook-slot-tile-host--already-booked]="isSlotAlreadyBookedByUser(slot)"
                       [attr.title]="isSlotAlreadyBookedByUser(slot) ? slotAlreadyBookedMessage : null">
                    <span *ngIf="isSlotAlreadyBookedByUser(slot)"
                          class="notebook-slot-tile-host__tooltip"
                          role="tooltip">{{ slotAlreadyBookedMessage }}</span>
                    <button type="button" class="notebook-slot-tile"
                            [class.notebook-slot-tile--already-booked]="isSlotAlreadyBookedByUser(slot)"
                            [class.notebook-slot-tile--booked]="isSlotBooked(slot) && !isSlotAlreadyBookedByUser(slot)"
                            [class.notebook-slot-tile--picked]="isNotebookScheduleSlotSelected(slot.key)"
                            [disabled]="isSlotEffectivelyDisabled(slot)"
                            [attr.aria-describedby]="isSlotAlreadyBookedByUser(slot) ? 'nb-slot-booked-' + slot.key : null"
                            (click)="toggleNotebookScheduleSlot(slot.key)">
                      <span class="notebook-slot-tile__label">{{ slot.startTime }} – {{ slot.endTime }}</span>
                      <span class="notebook-slot-tile__session-label">{{ formatSlotLabel(slot.label) }}</span>
                      <span class="notebook-slot-tile__seats">
                        <ng-container *ngIf="isGpuNotebookSchedule(); else cpuSlotSeatIcons">
                          <span class="notebook-slot-tile__seats-gpu-legend">
                            <span class="notebook-slot-tile__seat-count notebook-slot-tile__seat-count--open" [attr.aria-label]="getSlotOpenCount(slot) + ' free slots'">
                              <svg class="notebook-slot-tile__seat-icon notebook-slot-tile__seat-icon--open" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <circle cx="12" cy="7" r="4" fill="none" stroke="currentColor" stroke-width="1.8"/>
                                <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" fill="none"/>
                              </svg>
                              <span class="notebook-slot-tile__seat-count-value">{{ getSlotOpenCount(slot) }}</span>
                              <span class="notebook-slot-tile__seat-count-tooltip" role="tooltip">Free slots</span>
                            </span>
                            <span class="notebook-slot-tile__seat-count notebook-slot-tile__seat-count--booked" [attr.aria-label]="getSlotBookedCount(slot) + ' booked slots'">
                              <svg class="notebook-slot-tile__seat-icon notebook-slot-tile__seat-icon--taken" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <circle cx="12" cy="7" r="4" fill="currentColor" stroke="currentColor" stroke-width="1.8"/>
                                <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" fill="currentColor"/>
                              </svg>
                              <span class="notebook-slot-tile__seat-count-value">{{ getSlotBookedCount(slot) }}</span>
                              <span class="notebook-slot-tile__seat-count-tooltip" role="tooltip">Booked slots</span>
                            </span>
                          </span>
                        </ng-container>
                        <ng-template #cpuSlotSeatIcons>
                          <ng-container *ngFor="let available of slotSeatIcons(slot)">
                            <svg class="notebook-slot-tile__seat-icon" [class.notebook-slot-tile__seat-icon--open]="available" [class.notebook-slot-tile__seat-icon--taken]="!available" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <circle cx="12" cy="7" r="4" [attr.fill]="available ? 'none' : 'currentColor'" stroke="currentColor" stroke-width="1.8"/>
                              <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" [attr.fill]="available ? 'none' : 'currentColor'"/>
                            </svg>
                          </ng-container>
                          <span class="notebook-slot-tile__seats-label" *ngIf="!isSlotBooked(slot)">{{ slot.availableSlots }} open</span>
                          <span class="notebook-slot-tile__seats-label notebook-slot-tile__seats-label--full" *ngIf="isSlotBooked(slot)">All seats taken</span>
                        </ng-template>
                      </span>
                    </button>
                  </div>
                </div>
              </div>

            </ng-container>
            <p class="notebook-slot-empty" *ngIf="notebookForm.get('scheduleDate')?.value && !slotsLoading && availableSlots.length === 0">No sessions available for this date.</p>
            <p class="notebook-slot-footnote" *ngIf="notebookForm.get('scheduleDate')?.value && availableSlots.length > 0">
              Select up to 2 back-to-back sessions (8 hours total). Sessions must be consecutive.
            </p>
            <div class="error-message" *ngIf="(notebookForm.get('scheduleDate')?.invalid && notebookForm.get('scheduleDate')?.touched) || (!notebookForm.get('scheduleDate')?.value && notebookForm.get('scheduleSlots')?.touched)">Choose a date with open capacity</div>
            <div class="error-message" *ngIf="getScheduleSlotsFieldError()">{{ getScheduleSlotsFieldError() }}</div>
          </div>
        </div>

        <!-- Policy agreement — only shown in Step 4 -->
        <div class="nb-policy-row">
          <label class="policy-checkbox-label" [attr.for]="fieldPrefix + '-nb-policy'">
            <input type="checkbox" [id]="fieldPrefix + '-nb-policy'"
                   class="policy-checkbox checkbox-checkmark-custom"
                   [formControl]="$any(notebookForm.get('policyAgreed'))"
                   (change)="notebookForm.get('policyAgreed')?.setValue($any($event.target).checked); notebookForm.get('policyAgreed')?.markAsTouched()"
                   [class.error]="notebookForm.get('policyAgreed')?.invalid && notebookForm.get('policyAgreed')?.touched"/>
            <span class="policy-checkbox-text">I agree to the <a href="#" class="policy-link" (click)="openPolicyModal(); $event.preventDefault()">Policy terms and rules</a></span>
          </label>
          <div *ngIf="notebookForm.get('policyAgreed')?.invalid && notebookForm.get('policyAgreed')?.touched" class="error-message">You must agree to the policy terms and rules</div>
        </div>
      </div>

    </div><!-- /nb-hstepper__body -->
  </div><!-- /nb-hstepper -->
  </ng-template>
  <!-- Header Section with Tabs and Credits -->
  <div class="my-project-header-with-tabs">
    <!-- Tabs Navigation -->
    <div class="my-project-tabs">
    <button 
      class="my-project-tab"
      [class.active]="activeTab === 'list'"
      (click)="setActiveTab('list')"
      type="button"
    >
      Notebook List
    </button>
    <button 
      class="my-project-tab"
      [class.active]="activeTab === 'create'"
      (click)="setActiveTab('create')"
      type="button"
    >
      Create Notebook
    </button>
    <button
      *ngIf="requestCreditsEnabled"
      class="my-project-tab"
      [class.active]="activeTab === 'request-credits'"
      (click)="setActiveTab('request-credits')"
      type="button"
    >
      Request Credits
    </button>
  </div>

    <!-- Credits Card (Right Aligned) -->
    <div class="credits-card" *ngIf="requestCreditsEnabled">
      <!-- Skeleton Loader -->
      <ng-container *ngIf="creditsLoading">
        <div class="credits-icon skeleton-icon">
          <div class="skeleton-circle"></div>
        </div>
        <div class="credits-content">
          <div class="skeleton-text skeleton-label"></div>
          <div class="skeleton-text skeleton-value"></div>
          <div class="skeleton-text skeleton-expiry"></div>
        </div>
      </ng-container>

      <!-- Actual Content -->
      <ng-container *ngIf="!creditsLoading">
        <div class="credits-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 4H3C1.89543 4 1 4.89543 1 6V18C1 19.1046 1.89543 20 3 20H21C22.1046 20 23 19.1046 23 18V6C23 4.89543 22.1046 4 21 4Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M1 10H23" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="7" cy="15" r="1" fill="currentColor"/>
            <circle cx="10" cy="15" r="1" fill="currentColor"/>
            <circle cx="13" cy="15" r="1" fill="currentColor"/>
          </svg>
        </div>
        <div class="credits-content">
          <span class="credits-label">Credits Remaining</span>
          <span class="credits-value" [class.expired]="isCreditsExpired()">{{ creditsRemaining | number }}</span>
          <span class="credits-expiry" *ngIf="creditsExpirationDate" [class.expired]="isCreditsExpired()">
            <span *ngIf="creditsExpirationDate === 'NA'">Expires at: NA</span>
            <span *ngIf="creditsExpirationDate !== 'NA'">Expires: {{ creditsExpirationDate | date:'MMM dd, yyyy' }}</span>
          </span>
        </div>
      </ng-container>
    </div>
  </div>

  <!-- Persistent inline errors (My Projects — no auto-dismiss toast) -->
  <div
    *ngIf="myProjectFlowAlert as alert"
    class="my-project-flow-alert"
    [class.my-project-flow-alert--error]="alert.type === 'error'"
    [class.my-project-flow-alert--info]="alert.type === 'info'"
    role="alert"
    aria-live="polite"
  >
    <span class="my-project-flow-alert__icon" aria-hidden="true">
      <svg *ngIf="alert.type === 'error'" width="20" height="20" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
        <path d="M12 8v4M12 16h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
      <svg *ngIf="alert.type === 'info'" width="20" height="20" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
        <path d="M12 8v4M12 16h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
    </span>
    <p class="my-project-flow-alert__message">{{ alert.message }}</p>
    <button
      type="button"
      class="my-project-flow-alert__dismiss"
      (click)="clearMyProjectFlowAlert()"
      aria-label="Dismiss message"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
    </button>
  </div>

  <!-- Tab Content: Create Notebook -->
  <div class="my-project-tab-content" *ngIf="activeTab === 'create'">
    <div class="create-notebook-content notebook-create-shell">
      <form [formGroup]="notebookForm" class="notebook-form notebook-form--flow">
        <ng-container *ngTemplateOutlet="notebookCreateWorkspace; context: { fieldPrefix: 'nb-tab' }"></ng-container>
        <div class="form-actions form-actions--with-back" *ngIf="currentStep === 4">
          <button type="button" class="nb-hstepper__back-btn" (click)="previousStep()">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M14.5 17.5L9.5 12.5L14.5 7.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Back
          </button>
          <div class="form-actions__end">
            <button
              type="button"
              class="btn-submit"
              (click)="onSubmitNotebook()"
              [disabled]="loading || !isFormReadyToSubmit()"
            >
              <ng-container *ngIf="!loading">{{ isLocalBrowserInstanceType() ? 'Launch in browser' : 'Create Notebook' }}</ng-container>
              <ng-container *ngIf="loading">{{ isLocalBrowserInstanceType() ? 'Launching…' : 'Creating…' }}</ng-container>
            </button>
            <button
              type="button"
              class="btn-cancel"
              (click)="setActiveTab('list')"
              [disabled]="loading"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>

  <!-- Tab Content: Request Credits -->
  <div class="my-project-tab-content" *ngIf="requestCreditsEnabled && activeTab === 'request-credits'">
    <!-- Message when user doesn't have Compute role -->
    <div class="compute-role-message" *ngIf="!hasComputeRole">
      <div class="message-container">
        <svg class="message-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 8V12M12 16H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <div class="message-content">
          <h3 class="message-title">Compute Role Required</h3>
          <p class="message-text">You need to have Compute role in order to access Request Credits.</p>
          <button 
            class="upgrade-role-btn"
            (click)="openUpgradeRoleModal()"
            type="button"
          >
            Upgrade Your Role
          </button>
        </div>
      </div>
    </div>
    
    <!-- Message when Compute role is only pending (not yet approved) - do not show request form -->
    <div class="compute-role-message" *ngIf="hasComputeRole && !hasActiveComputeRole">
      <div class="message-container">
        <svg class="message-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 8V12M12 16H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <div class="message-content">
          <h3 class="message-title">Compute Role Pending</h3>
          <p class="message-text">Your Compute role request is pending approval. You can request credits once it is approved.</p>
        </div>
      </div>
    </div>
    
    <!-- Pending credit request warning (same as Join organisation / data-provider-entity pending request info) -->
    <div *ngIf="hasActiveComputeRole && hasPendingCreditRequest && !pendingCreditRequestLoading" class="pending-request-info-wrapper">
      <div class="pending-request-info">
        <div class="pending-request-info--icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M12 16V12M12 8H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="pending-request-info--text">
          <strong>Pending Request.</strong> You already have a pending credit request.<br>
          Please wait for approval before submitting another.
        </div>
        <button
          type="button"
          class="btn-withdraw-entity"
          [disabled]="withdrawCreditLoading"
          (click)="openWithdrawCreditModal()"
        >
          {{ withdrawCreditLoading ? 'Withdrawing...' : 'Withdraw Request' }}
        </button>
      </div>
    </div>
    
    <!-- Load Request credits: open modal with all requests (granted, rejected, pending) when there are no pending requests -->
    <div *ngIf="hasActiveComputeRole && !hasPendingCreditRequest && !pendingCreditRequestLoading" class="request-credits-history-row">
      <button
        type="button"
        class="btn-load-request-credits"
        (click)="openRequestCreditsHistoryModal()"
      >
      Browse Request History
      </button>
    </div>
    
    <!-- Request Credits Form (only show when user has active Compute role, not just pending). Prepopulate with pending request data when available. -->
    <app-request-credits
      *ngIf="hasActiveComputeRole"
      [hasPendingRequest]="hasPendingCreditRequest"
      [initialData]="pendingCreditRequestData"
      (requestSubmitted)="onCreditRequestSubmitted()"
    ></app-request-credits>
  </div>

  <!-- Tab Content: Notebook List -->
  <div class="my-project-tab-content" *ngIf="activeTab === 'list'">
    <div class="create-notebook-content notebook-create-shell">
      <div
        class="notebook-form--flow notebook-list-flow"
        [class.notebook-list-flow--empty]="!loading && notebookTableRows.length === 0"
      >

    <!-- ── Filters panel (Activities-style) ─────────────────────────────── -->
    <app-filter-panel
      [fields]="notebookListFilterFields"
      [defaultExpanded]="false"
      (filterChange)="onNotebookListFilterChange($event)"
      (clearFilters)="onNotebookListClearFilters()"
    ></app-filter-panel>

    <!-- ── Cell templates (resolved by ViewChild) ──────────────────────── -->
    <ng-template #nbNotebookCell let-row>
      <div class="nb-table__cell-name">
        <div class="nb-table__name-wrap">
          <span class="nb-table__name">{{ row._card.displayTitle }}</span>
        </div>
      </div>
    </ng-template>

    <ng-template #nbStatusCell let-row>
      <span class="nb-table__status" [ngClass]="'nb-table__status--' + getNotebookStatusClass(row._card)">
        <span class="nb-table__status-dot"></span>
        <ng-container *ngIf="getNotebookStatusClass(row._card) !== 'opening'; else nbOpeningLabel">{{ row._card.statusBadge }}</ng-container>
        <ng-template #nbOpeningLabel>
          <span class="nb-table__status-opening">
            <span class="nb-table__status-dots" aria-hidden="true"></span>
            {{ row._card.statusBadge }}
          </span>
        </ng-template>
      </span>
    </ng-template>

    <ng-template #nbDurationCell let-row>
      <span class="nb-table__duration-text">{{ row.duration }}</span>
    </ng-template>

    <ng-template #nbActionsCell let-row>
      <div
        class="nb-table__cell-actions action-cell-wrapper"
        [class.action-dropdown-open]="isNotebookActionDropdownOpen(row._card.id)"
      >
        <button
          type="button"
          class="action-btn"
          appActionDropdownTrigger
          [class.active]="isNotebookActionDropdownOpen(row._card.id)"
          [disabled]="isNotebookRowActionDisabled(row._card)"
          [attr.aria-disabled]="isNotebookRowActionDisabled(row._card)"
          [attr.title]="isNotebookRowActionDisabled(row._card) ? getNotebookRowActionDisabledTitle(row._card) : null"
          (click)="toggleNotebookActionDropdown($event, row._card)"
        >
          Action
        </button>
      </div>
    </ng-template>

    <!-- ── app-table ────────────────────────────────────────────────────── -->
    <app-table
      [columns]="notebookTableColumns"
      [data]="notebookTableRows"
      [loading]="loading"
      [emptyMessage]="emptyMessage"
      [pagination]="notebookListPagination"
      [dropdownOpenRowIndex]="openNotebookActionDropdownRowIndex"
      (pageChange)="onNotebookListPageChange($event)"
      (pageSizeChange)="onNotebookListPageSizeChange($event)"
      [bordered]="true"
      [hoverable]="true"
      [striped]="false"
      [scrollable]="true"
      scrollHeight="none"
    ></app-table>

    <!-- Fixed overlay on top of table — not clipped by scroll container (e.g. single-row list) -->
    <div
      *ngIf="notebookActionOverlayPosition && openNotebookActionCard && !isNotebookRowActionDisabled(openNotebookActionCard)"
      class="action-dropdown-overlay notebook-action-dropdown-overlay"
      role="menu"
      aria-label="Notebook actions"
      [class.action-dropdown-overlay--above]="notebookActionOverlayPosition.openAbove"
      [style.top.px]="notebookActionOverlayPosition.top"
      [style.left.px]="notebookActionOverlayPosition.left"
      [style.right.px]="notebookActionOverlayPosition.right"
      (click)="$event.stopPropagation()"
    >
      <ul class="dropdown-list">
        <ng-container *ngIf="showNotebookActiveLifecycleActions(openNotebookActionCard)">
          <li class="dropdown-item">
            <button type="button" role="menuitem" class="dropdown-link" (click)="onNotebookCardOpen(openNotebookActionCard); closeNotebookActionDropdown()">Open</button>
          </li>
          <li class="dropdown-item">
            <button
              type="button"
              role="menuitem"
              class="dropdown-link"
              [disabled]="isNotebookExtendDisabled(openNotebookActionCard)"
              [attr.aria-disabled]="isNotebookExtendDisabled(openNotebookActionCard)"
              [attr.title]="getNotebookExtendDisabledTitle(openNotebookActionCard)"
              (click)="onNotebookActionExtend(openNotebookActionCard)"
            >
              Extend
            </button>
          </li>
          <li class="dropdown-item" *ngIf="!openNotebookActionCard.isMock">
            <button type="button" role="menuitem" class="dropdown-link" (click)="onNotebookActionDelete(openNotebookActionCard)">Delete</button>
          </li>
        </ng-container>

        <ng-container *ngIf="showNotebookCancelBookingAction(openNotebookActionCard)">
          <li class="dropdown-item">
            <button type="button" role="menuitem" class="dropdown-link" (click)="onNotebookCardCancelBooking(openNotebookActionCard); closeNotebookActionDropdown()">Cancel Booking</button>
          </li>
        </ng-container>

        <ng-container *ngIf="showNotebookResetBookingAction(openNotebookActionCard)">
          <li class="dropdown-item">
            <button type="button" role="menuitem" class="dropdown-link" (click)="onNotebookCardResetBooking(openNotebookActionCard); closeNotebookActionDropdown()">Reset Booking</button>
          </li>
        </ng-container>
      </ul>
    </div>

      </div>
    </div>
  </div>

  <!-- Notebook List: Stopping -->
  <app-modal
    [(visible)]="showNotebookStoppingModal"
    [showHeader]="false"
    [showFooter]="false"
    [closable]="false"
    [styleClass]="'custom-modal notebook-spotlight-modal'"
    [width]="'420px'"
    (onClose)="closeNotebookStoppingModal()"
  >
    <div class="notebook-spotlight">
      <button type="button" class="notebook-spotlight__close" (click)="closeNotebookStoppingModal()" aria-label="Close">×</button>
      <div class="notebook-spotlight__icon notebook-spotlight__icon--stop" aria-hidden="true">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="7" y="7" width="10" height="10" rx="1" fill="#fff"/>
        </svg>
      </div>
      <h2 class="notebook-spotlight__title">Stopping Notebook</h2>
      <p class="notebook-spotlight__text">{{ notebookStoppingSubline }}</p>
    </div>
  </app-modal>

  <!-- Notebook List: Extend session -->
  <app-modal
    [(visible)]="showNotebookExtendModal"
    [showHeader]="false"
    [showFooter]="false"
    [closable]="true"
    [styleClass]="'custom-modal notebook-spotlight-modal nb-extend-dialog'"
    [width]="'540px'"
    (onClose)="closeNotebookExtendModal()"
  >
    <div class="nb-extend-modal">
      <div class="nb-extend-modal__leaf" aria-hidden="true">
        <img src="/assets-leaf.png" alt="" />
      </div>
      <div class="nb-extend-modal__icon" aria-hidden="true">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/>
          <path d="M12 7v6l4 2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </div>
      <h2 class="nb-extend-modal__title">Extend Session</h2>
      <p class="nb-extend-modal__text">{{ extendSessionDescription }}</p>
      <div class="nb-extend-modal__rows">
        <div class="nb-extend-modal__row">
          <span class="nb-extend-modal__row-label">Current End Time</span>
          <span class="nb-extend-modal__row-value">{{ extendModalCopy.currentEnd }}</span>
        </div>
        <div class="nb-extend-modal__row">
          <span class="nb-extend-modal__row-label">Extended End Time</span>
          <span class="nb-extend-modal__row-value">{{ extendModalCopy.extendedEnd }}</span>
        </div>
      </div>
      <div class="nb-extend-modal__actions">
        <button type="button" class="nb-extend-modal__btn nb-extend-modal__btn--cancel" (click)="closeNotebookExtendModal()">
          Cancel
        </button>
        <button type="button" class="nb-extend-modal__btn nb-extend-modal__btn--confirm" (click)="confirmNotebookExtendModal()">
          Confirm Extension
        </button>
      </div>
    </div>
  </app-modal>

  <!-- Notebook List: Extend unavailable -->
  <app-modal
    [(visible)]="showNotebookExtendUnavailableModal"
    [showHeader]="false"
    [showFooter]="false"
    [closable]="false"
    [styleClass]="'custom-modal notebook-spotlight-modal'"
    [width]="'420px'"
    (onClose)="closeNotebookExtendUnavailableModal()"
  >
    <div class="notebook-spotlight">
      <button type="button" class="notebook-spotlight__close" (click)="closeNotebookExtendUnavailableModal()" aria-label="Close">×</button>
      <div class="notebook-spotlight__icon notebook-spotlight__icon--warn" aria-hidden="true">!</div>
      <h2 class="notebook-spotlight__title">Extension Unavailable</h2>
      <p class="notebook-spotlight__text">
        You cannot extend the current slot because the next consecutive slot for this resource is already booked by another user.
      </p>
      <button type="button" class="notebook-spotlight__btn notebook-spotlight__btn--primary" (click)="closeNotebookExtendUnavailableModal()">
        Understood
      </button>
    </div>
  </app-modal>

  <!-- Notebook List: Cancel booking -->
  <app-confirmation-dialog
    [(visible)]="showNotebookCancelBookingModal"
    type="warning"
    title="This Action Will Cancel Your Booking"
    message="Are you sure you want to cancel this booking? The notebook will not be created and the reserved compute slot will be released. This action cannot be undone."
    confirmLabel="Yes, Cancel Booking"
    cancelLabel="Keep Booking"
    themeColor="red"
    width="540px"
    (onConfirm)="confirmNotebookCancelBooking()"
    (onCancel)="closeNotebookCancelBookingModal()"
    (onClose)="closeNotebookCancelBookingModal()"
  ></app-confirmation-dialog>

  <!-- Notebook List: Delete notebook -->
  <app-confirmation-dialog
    [(visible)]="showNotebookDeleteConfirmModal"
    type="delete"
    title="This Action Will Permanently Delete Your Notebook"
    message="Deleting this notebook will release the booked slot. If you need it again, you will have to book the slot again."
    confirmLabel="Delete Notebook"
    cancelLabel="Cancel"
    themeColor="red"
    width="540px"
    (onConfirm)="confirmNotebookDeleteModal()"
    (onCancel)="closeNotebookDeleteConfirmModal()"
    (onClose)="closeNotebookDeleteConfirmModal()"
  ></app-confirmation-dialog>

<!-- Create Notebook Modal -->
<app-modal
  [(visible)]="showCreateNotebookModal"
  header="Notebook Information"
  [width]="'920px'"
  [showFooter]="currentStep === 4"
  [confirmLabel]="'Submit'"
  [cancelLabel]="'Cancel'"
  [confirmButtonDisabled]="loading"
  (onConfirm)="onSubmitNotebook()"
  (onCancel)="onModalCancel()"
  (onClose)="onModalClose()"
>
  <div class="create-notebook-modal-content notebook-create-shell notebook-create-shell--modal">
    <form [formGroup]="notebookForm" class="notebook-form notebook-form--flow">
      <ng-container *ngTemplateOutlet="notebookCreateWorkspace; context: { fieldPrefix: 'nb-modal' }"></ng-container>
      <div class="note-message note-message--compact">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M12 16V12M12 8H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>Provisioning can take a minute or two after you submit.</span>
      </div>
    </form>
    <div class="modal-leaf-pattern">
      <img src="/assets-leaf.png" alt="" class="modal-leaf-image" />
    </div>
  </div>
</app-modal>

  <!-- Policy Terms Modal -->
  <app-modal
    [(visible)]="showPolicyModal"
    header="Policy Terms and Rules"
    [width]="'800px'"
    [showFooter]="false"
    (visibleChange)="showPolicyModal = $event"
  >
    <div class="policy-modal-content">
      <div class="policy-content">
        <h3>1. Policy Guidelines</h3>
        <p>This usage policy outlines the guidelines for utilizing the MahaAgX Sandbox environment. Designed for flexibility and high-performance data science, this policy ensures equitable resource distribution and data security for all users.</p>

        <h4>1.1. Access & Authentication</h4>
        <ul>
          <li><strong>Credit-Based Model:</strong> Access to GPU resources is governed by a credit system.</li>
          <li><strong>Credit Allocation:</strong> Credits are not granted automatically. They will be allocated to users following an evaluation of usage requirements, which must be submitted via the official Credit Request Form.</li>
          <li><strong>Compute Role Approval:</strong> To access and launch any Notebook environment, a user must have a Compute Role explicitly approved by the administrator.</li>
          <li><strong>On-Demand GPU Availability:</strong> Once credits are allocated, GPU-enabled notebooks (if available) can be accessed immediately without prior booking.</li>
          <li><strong>Credit-Free CPU Access:</strong> Standard CPU Notebooks are credit-free and may be used for general development tasks, subject to hardware availability.</li>
        </ul>

        <h4>1.2. Resource Allocation & GPU Selection</h4>
        <ul>
          <li><strong>Choice of Hardware:</strong> Users may select from various GPU configuration options based on the complexity of their models.</li>
          <li><strong>Fair Use:</strong> MahaAgX reserves the right to manage concurrent sessions to ensure system stability for the entire user community.</li>
        </ul>

        <h4>1.3. Data Retention & Management</h4>
        <ul>
          <li><strong>Persistent Storage:</strong> Work is not deleted at the end of a session. Data is retained in the environment as long as the user does not manually delete the Notebook.</li>
          <li><strong>User Responsibility:</strong> Users are responsible for managing their storage and deleting unused notebooks to free up system resources.</li>
        </ul>

        <h4>1.4. API & Integration Policy</h4>
        <ul>
          <li><strong>Custom Playbooks:</strong> Users have the flexibility to use Custom Playbooks for API-based data access directly within the IDE, unlike more restrictive environments.</li>
          <li><strong>Security:</strong> Users must follow best practices for credential management, ensuring API keys are not exposed in shared code.</li>
        </ul>

        <h4>1.5. Prohibited Activities</h4>
        <p>Users are strictly prohibited from using Sandbox resources for:</p>
        <ul>
          <li>Cryptocurrency mining or non-research-related compute tasks.</li>
          <li>Unauthorized network scanning or security testing.</li>
          <li>Circumventing the credit-request evaluation process.</li>
        </ul>

        <h4>1.6. Support & Maintenance</h4>
        <ul>
          <li><strong>Idle Termination:</strong> To preserve your allocated credits, sessions that remain idle for an extended period may be automatically terminated.</li>
          <li><strong>System Updates:</strong> Periodic maintenance windows will be communicated via the platform dashboard.</li>
        </ul>

        <h3>2. Rules and Limitations</h3>

        <h4>2.1. Compute & Resource Usage</h4>
        <ul>
          <li><strong>Idle Resource Policy:</strong> While CPU notebooks are credit-free, leaving idle sessions running is prohibited. Active kernels with no computational activity for more than 60 minutes may be automatically stopped to free up allocation for other users.</li>
          <li><strong>Commercial Use Restriction:</strong> The Sandbox is for research, development, and prototyping. Running production-grade high-traffic applications or commercial APIs directly from the Sandbox without a commercial agreement is not permitted.</li>
        </ul>

        <h4>2.2. Credit & Role Governance</h4>
        <ul>
          <li><strong>Credit Transparency:</strong> Credits allocated via the Credit Request Form are strictly for the project described. Using research credits for unrelated personal projects or unauthorized external consulting is a violation of the terms.</li>
          <li><strong>Role-Based Constraints:</strong> Access is tied to the Compute Role. If a user's role status changes or expires, access to existing notebooks and data may be locked until re-approval.</li>
          <li><strong>Request Accuracy:</strong> Users must provide realistic estimations in their credit request form. Consistent over-estimation or intentional under-utilization of requested high-end GPUs may lead to lower priority in future allocations.</li>
        </ul>

        <h4>2.3. Data & Persistence Limitations</h4>
        <ul>
          <li><strong>Storage Quotas:</strong> Although data is retained until deleted by the user, each notebook environment has a soft storage limit (e.g., 50GB). Exceeding this limit will prevent new data from being saved until space is cleared.</li>
          <li><strong>Persistence Integrity:</strong> While MahaAgX retains your notebooks, this is not a permanent backup service. MahaAgX is not liable for data loss due to system-wide upgrades or hardware failure. Users should export critical code to external repositories (e.g., GitHub/GitLab).</li>
          <li><strong>Data Privacy:</strong> Users must not store or process sensitive Personal Identifiable Information (PII) or highly classified data without explicit security clearance and encryption.</li>
        </ul>

        <h4>2.4. Prohibited Technical Actions</h4>
        <ul>
          <li><strong>Cryptomining:</strong> Any form of cryptocurrency mining is strictly prohibited and will result in immediate and permanent account termination.</li>
          <li><strong>Network Probing:</strong> Using the Sandbox for port scanning, IP spoofing, or launching network-based attacks (DDoS) is strictly forbidden.</li>
          <li><strong>Software Piracy:</strong> Running or storing "cracked" software or unlicensed commercial libraries within the IDE is prohibited.</li>
        </ul>
      </div>
    </div>
  </app-modal>

  <!-- Navigation Guard Modal -->
  <app-modal
    [(visible)]="showNavigationGuardModal"
    header="Notebooks Are Opening"
    [width]="'500px'"
    [showFooter]="false"
    (visibleChange)="showNavigationGuardModal = $event"
  >
    <div class="navigation-guard-modal-content">
      <div class="navigation-guard-message">
        <p>You have <strong>{{ openingNotebooks.length }}</strong> notebook(s) that are currently opening:</p>
        <ul class="opening-notebooks-list">
          <li *ngFor="let notebook of openingNotebooks">
            <strong>{{ notebook.notebookName }}</strong>
          </li>
        </ul>
        <p>Please wait until the Notebook is opened or Delete the notebook before navigating away.</p>
      </div>
      
      <div class="navigation-guard-actions">
        <button 
          class="navigation-guard-btn navigation-guard-btn--wait"
          (click)="handleWaitForNotebooksNavigation()"
          type="button"
        >
          Continue to wait
        </button>
        <button 
          class="navigation-guard-btn navigation-guard-btn--delete"
          (click)="handleDeleteOpeningNotebooksNavigation()"
          type="button"
        >
          Delete Notebook
        </button>
      </div>
    </div>
  </app-modal>

  <!-- Withdraw Credit Request Confirmation (replica of Join org withdraw modal) -->
  <app-confirmation-dialog
    [(visible)]="showWithdrawCreditModal"
    type="warning"
    title="Withdraw Request"
    message="Are you sure you want to withdraw your pending credit request? You can submit a new request after withdrawing."
    confirmLabel="Withdraw"
    cancelLabel="Cancel"
    themeColor="orange"
    width="480px"
    [loading]="withdrawCreditLoading"
    (onConfirm)="confirmWithdrawCreditRequest()"
    (onCancel)="closeWithdrawCreditModal()"
    (onClose)="closeWithdrawCreditModal()"
  ></app-confirmation-dialog>

  <!-- Notebook Ready Confirmation Modal -->
  <app-confirmation-dialog
    [(visible)]="showNotebookReadyModal"
    type="success"
    [title]="notebookReadyModalHeading"
    [message]="notebookReadyModalSubline"
    [confirmLabel]="notebookReadyModalCta"
    [showCancelButton]="false"
    themeColor="green"
    width="540px"
    (onConfirm)="onProceedToNotebook()"
    (onClose)="closeNotebookReadyModal()"
  ></app-confirmation-dialog>

  <!-- Request Credits History Modal: granted, rejected, pending -->
  <app-modal
    [(visible)]="showRequestCreditsHistoryModal"
    header="Request Credits History"
    [width]="'900px'"
    [showFooter]="false"
    (onClose)="closeRequestCreditsHistoryModal()"
  >
    <div class="request-credits-history-modal-content">
      <div *ngIf="creditRequestHistoryLoading" class="history-loading">Loading...</div>
      <div *ngIf="!creditRequestHistoryLoading && creditRequestHistory.length === 0" class="history-empty">No request credits history found.</div>
      <div *ngIf="!creditRequestHistoryLoading && creditRequestHistory.length > 0" class="history-table-wrapper">
        <table class="history-table">
          <thead>
            <tr>
              <th>Requested On</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of creditRequestHistory; let i = index" [class.dropdown-open]="openHistoryActionIndex === i">
              <td>{{ row.requestedOn }}</td>
              <td><span [ngClass]="getStatusChipClass(row.status)">{{ row.status }}</span></td>
              <td class="action-cell">
                <button 
                  class="action-btn" 
                  (click)="toggleHistoryActionDropdown($event, i)"
                  type="button"
                >
                  Action
                </button>
                <div class="action-dropdown" *ngIf="openHistoryActionIndex === i" (click)="$event.stopPropagation()">
                  <button class="action-dropdown-item" (click)="fillFromHistory(row)" type="button">
                    Fill
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </app-modal>
